import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { fmtRoDate } from "../../lib/dateFmt";
import { toISO, addDays, mondayOf } from "./utils";

/**
 * Dialog universal pentru editarea necesarului de persoane (min+max).
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - target: { kind: "regie" | "project", id: string, name: string }
 *  - isoDay: "YYYY-MM-DD" — ziua de start (luni-ul săptămânii curente se derivă)
 *  - currentMin: number — valoarea curentă afișată (pentru info)
 *  - currentMax?: number
 *  - onSaved: () => void
 */
export default function EditMinPeopleDialog({ open, onClose, target, isoDay, currentMin, currentMax, onSaved }) {
  const [minP, setMinP] = useState("");
  const [scope, setScope] = useState("day"); // "day" | "week" | "forever"
  const [projEnd, setProjEnd] = useState(null); // end_date proiect — "de acum încolo" = până la finalul proiectului
  useEffect(() => {
    if (open && target?.kind === "project" && target?.id) {
      api.get(`/projects/${target.id}`).then((r) => setProjEnd(r.data?.end_date || null)).catch(() => setProjEnd(null));
    } else {
      setProjEnd(null);
    }
  }, [open, target?.kind, target?.id]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setMinP(currentMin != null && currentMin > 0 ? String(currentMin) : "");
      setScope("day");
    }
  }, [open, currentMin, currentMax]);

  if (!target) return null;

  const weekMonday = isoDay ? toISO(mondayOf(new Date(isoDay + "T00:00:00"))) : null;
  const weekSunday = weekMonday ? toISO(addDays(new Date(weekMonday + "T00:00:00"), 6)) : null;

  const submit = async () => {
    const minNum = minP.trim() === "" ? null : parseInt(minP, 10);
    const maxNum = minNum; // se completează doar minimul; maximul urmează minimul
    if (minNum != null && (isNaN(minNum) || minNum < 0)) { toast.error("Min invalid"); return; }
    setBusy(true);
    try {
      const body = {};
      if (minNum != null) body.min_people = minNum;
      if (maxNum != null) body.max_people = maxNum;
      const isEmpty = minNum == null && maxNum == null;

      if (scope === "day") {
        const url = target.kind === "regie" ? `/regii/${target.id}/days/${isoDay}` : `/projects/${target.id}/days/${isoDay}`;
        // dacă ambele goale → backend șterge override-ul
        await api.put(url, isEmpty ? {} : body);
        toast.success(isEmpty ? "Override zilnic eliminat" : `Necesar pe ${fmtRoDate(isoDay)}: ${minNum ?? "—"} persoane`);
      } else if (scope === "forever") {
        // Pentru regii: modifică default-ul structural + curăță week-overrides viitoare
        if (target.kind === "regie") {
          if (isEmpty) {
            toast.error("Pentru valabil de azi pe perioadă nedeterminată, introdu cel puțin minim personal");
            setBusy(false);
            return;
          }
          // 1. Update default_min_people / default_max_people pe regie
          //    PUT /regii/{id} folosește RegieIn (necesită toate câmpurile required),
          //    deci fetch-uim regia curentă și merge-uim valorile noi.
          const current = await api.get(`/regii/${target.id}`).then((r) => r.data);
          const regieUpdate = { ...current };
          delete regieUpdate.id;
          delete regieUpdate.created_at;
          if (minNum != null) regieUpdate.default_min_people = minNum;
          if (maxNum != null) regieUpdate.default_max_people = maxNum;
          await api.put(`/regii/${target.id}`, regieUpdate);
          // 2. Aplică ca week-override pe toate săptămânile viitoare (52 săpt. = 1 an)
          const startMonday = mondayOf(new Date(isoDay + "T00:00:00"));
          const promises = [];
          for (let i = 0; i < 52; i++) {
            const wk = toISO(addDays(startMonday, i * 7));
            promises.push(api.put(`/regii/${target.id}/weeks/${wk}`, body).catch(() => null));
          }
          await Promise.all(promises);
          toast.success(`Necesar setat de azi pe perioadă nedeterminată: ${minNum} persoane`);
        } else {
          // Pentru proiecte: aplică day-overrides pe toate zilele de azi până la sfârșitul proiectului
          // sau pe 12 săptămâni (3 luni) ca aproximare a „perioadei nedeterminate"
          if (isEmpty) {
            toast.error("Pentru valabil de azi pe perioadă nedeterminată, introdu cel puțin minim personal");
            setBusy(false);
            return;
          }
          const startDay = new Date(isoDay + "T00:00:00");
          const days = [];
          if (projEnd && projEnd >= isoDay) {
            // până la finalul proiectului (inclusiv)
            for (let i = 0; i <= 366; i++) {
              const iso2 = toISO(addDays(startDay, i));
              if (iso2 > projEnd) break;
              days.push(iso2);
            }
          } else {
            for (let i = 0; i < 84; i++) days.push(toISO(addDays(startDay, i))); // fallback: proiect fără dată de sfârșit
          }
          await Promise.all(days.map((d) => api.put(`/projects/${target.id}/days/${d}`, body).catch(() => null)));
          toast.success(projEnd && projEnd >= isoDay
            ? `Necesar ${minNum} pers. setat de la ${fmtRoDate(isoDay)} până la finalul proiectului (${fmtRoDate(projEnd)})`
            : `Necesar setat pentru ${days.length} zile începând de la ${fmtRoDate(isoDay)}: ${minNum} persoane`);
        }
      } else {
        // scope === "week": aplică pe toate cele 7 zile (Lu-Du)
        let autoCleared = null;
        if (target.kind === "regie") {
          // pentru regii avem endpoint dedicat
          const { data: resp } = await api.put(`/regii/${target.id}/weeks/${weekMonday}`, isEmpty ? {} : body);
          autoCleared = resp?.auto_cleared || null;
        } else {
          // pentru proiecte: aplică day-overrides pe fiecare zi (fără a depăși finalul proiectului)
          const days = [];
          for (let i = 0; i < 7; i++) {
            const iso2 = toISO(addDays(new Date(weekMonday + "T00:00:00"), i));
            if (projEnd && iso2 > projEnd) break;
            days.push(iso2);
          }
          const url = (d) => `/projects/${target.id}/days/${d}`;
          await Promise.all(days.map((d) => api.put(url(d), isEmpty ? {} : body)));
        }
        const weekEndShown = (target.kind === "project" && projEnd && projEnd < weekSunday) ? projEnd : weekSunday;
        toast.success(isEmpty
          ? `Override eliminat pentru săptămâna ${fmtRoDate(weekMonday)} – ${fmtRoDate(weekEndShown)}`
          : `Necesar săpt. ${fmtRoDate(weekMonday)} – ${fmtRoDate(weekEndShown)}: ${minNum ?? "—"} persoane`);
        if (autoCleared && autoCleared.persons > 0) {
          toast.warning(
            `⚠ Max = 0: scoase automat ${autoCleared.persons} ${autoCleared.persons === 1 ? "persoană" : "persoane"} din ${autoCleared.entries} ${autoCleared.entries === 1 ? "alocare" : "alocări"} pentru această săptămână.`,
            { duration: 6000 }
          );
        }
      }
      onSaved && onSaved();
      onClose && onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la salvare");
    } finally {
      setBusy(false);
    }
  };

  const clearAll = () => { setMinP(""); };

  return (
    <Dialog open={!!open} onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="edit-min-people-dialog">
        <DialogHeader>
          <DialogTitle>Modifică necesarul de persoane</DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Setează minimul de persoane necesare pentru această zi, săptămână sau pe perioadă nedeterminată.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-md bg-zinc-50 border border-zinc-200 p-2 text-sm">
            <div className="font-semibold text-zinc-800">
              {target.kind === "regie" ? "🟧 " : "📁 "}{target.name || "—"}
            </div>
            <div className="text-xs text-zinc-500 mt-0.5">
              Necesar actual: <strong>{currentMin ?? 0}</strong>
              {currentMax != null && currentMax > 0 ? <> · max <strong>{currentMax}</strong></> : null}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Minim personal</Label>
              <Input
                type="number"
                min={0}
                value={minP}
                onChange={(e) => setMinP(e.target.value)}
                placeholder="ex: 3"
                data-testid="edit-min-people-input-min"
                autoFocus
              />
              <div className="text-[10px] text-zinc-400 mt-0.5">gol = revine la default</div>
            </div>
          </div>

          <div>
            <Label className="text-xs">Aplică pe:</Label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-1.5">
              <button
                type="button"
                onClick={() => setScope("day")}
                className={`px-3 py-2 rounded border-2 text-sm font-semibold transition-all ${
                  scope === "day"
                    ? "border-amber-500 bg-amber-50 text-amber-900"
                    : "border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50"
                }`}
                data-testid="edit-min-people-scope-day"
              >
                📅 Doar pe<br/>
                <span className="text-[10px] font-normal opacity-80">{fmtRoDate(isoDay)}</span>
              </button>
              <button
                type="button"
                onClick={() => setScope("week")}
                className={`px-3 py-2 rounded border-2 text-sm font-semibold transition-all ${
                  scope === "week"
                    ? "border-blue-500 bg-blue-50 text-blue-900"
                    : "border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50"
                }`}
                data-testid="edit-min-people-scope-week"
              >
                🗓 Săptămâna<br/>
                <span className="text-[10px] font-normal opacity-80">{fmtRoDate(weekMonday)} – {fmtRoDate(weekSunday)}</span>
              </button>
              <button
                type="button"
                onClick={() => setScope("forever")}
                className={`px-3 py-2 rounded border-2 text-sm font-semibold transition-all ${
                  scope === "forever"
                    ? "border-emerald-500 bg-emerald-50 text-emerald-900"
                    : "border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50"
                }`}
                data-testid="edit-min-people-scope-forever"
                title={target.kind === "regie" ? "Modifică default-ul regiei + aplică 1 an înainte" : (projEnd ? `Aplică de azi până la finalul proiectului (${fmtRoDate(projEnd)})` : "Aplică pe 12 săptămâni (3 luni) începând de azi")}
              >
                ♾ De azi în colo<br/>
                <span className="text-[10px] font-normal opacity-80">{target.kind === "project" && projEnd ? `până la final (${fmtRoDate(projEnd)})` : "perioadă nedeterminată"}</span>
              </button>
            </div>
          </div>

          {scope === "forever" && target.kind === "regie" && (
            <div className="text-[11px] bg-emerald-50 border border-emerald-200 rounded p-2 text-emerald-900">
              ♾ Va modifica <strong>default-ul regiei</strong> ({target.name}) + va aplica valoarea pe toate săptămânile viitoare (1 an înainte).
            </div>
          )}
          {scope === "forever" && target.kind === "project" && (
            <div className="text-[11px] bg-emerald-50 border border-emerald-200 rounded p-2 text-emerald-900">
              {projEnd
                ? <>♾ Va aplica valoarea zilnic de la {fmtRoDate(isoDay)} <strong>până la finalul proiectului ({fmtRoDate(projEnd)})</strong>.</>
                : <>♾ Proiect fără dată de sfârșit — valoarea se aplică pe <strong>84 de zile (12 săptămâni)</strong> de la {fmtRoDate(isoDay)}.</>}
            </div>
          )}

          {scope === "week" && target.kind === "project" && (
            <div className="text-[11px] bg-blue-50 border border-blue-200 rounded p-2 text-blue-900">
              ℹ La proiecte, valoarea se aplică zilnic pe săptămâna curentă{projEnd && projEnd < weekSunday ? <>, dar <strong>doar până la finalul proiectului ({fmtRoDate(projEnd)})</strong></> : ""}.
            </div>
          )}
          {scope === "week" && target.kind === "regie" && (
            <div className="text-[11px] bg-blue-50 border border-blue-200 rounded p-2 text-blue-900">
              ℹ La regii, valoarea se salvează ca override pe săptămâna {fmtRoDate(weekMonday)} – {fmtRoDate(weekSunday)} (peste default-ul săptămânal).
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" type="button" onClick={clearAll} data-testid="edit-min-people-clear">
            🗑 Golește (default)
          </Button>
          <Button variant="outline" type="button" onClick={onClose} disabled={busy}>Anulează</Button>
          <Button type="button" onClick={submit} disabled={busy} data-testid="edit-min-people-save">
            {busy ? "Se salvează..." : "💾 Salvează"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
